import {
  NotificationClickEvent,
  NotificationWillDisplayEvent,
  OneSignal,
  OSNotification,
} from 'react-native-onesignal';
import notifee from '@notifee/react-native';
import Settings from '../../../Settings';
import Events from '../../../logging/Events';
import NavActions from '../../../actions/NavActions';
import AppRoutes from '../../../AppRoutes';
import AccountStore from '../../../stores/AccountStore';
import {Platform} from 'react-native';
import {store} from '../../../redux/store';
import {swicthCurrentCampusesAndLocations} from '../../../redux/slices/campusLocationSlice';

interface ExtendedNotification extends OSNotification {
  additionalData?: {
    announcementId?: string;
    [key: string]: unknown;
  };
}

export class OneSignalAdapter {
  private notificationHandlers: {
    foreground?: (event: NotificationWillDisplayEvent) => void;
    click?: (event: NotificationClickEvent) => void;
  } = {};

  constructor(private logError: (msg: string, err?: unknown) => void) {}

  async initialize() {
    const OneSignalAppId = Settings.oneSignalAppId;

    try {
      OneSignal.initialize(OneSignalAppId);
      Events.NotificationEvent.trackOneSignalId(OneSignalAppId);
    } catch (err) {
      this.logError('[OneSignal] initialization failed', err);
      return;
    }

    await this.requestPermission();

    if (Platform.OS === 'ios') {
      try {
        (OneSignal as any).LiveActivities?.setupDefault?.();
        (OneSignal as any).LiveActivities?.setup?.('OrderTrackerAttributes');
      } catch (err) {
        this.logError('[OneSignal] LiveActivities setup failed', err);
      }
    }
    await this.syncUserTags();
    this.registerListeners();
  }
  async refreshTags() {
    await this.syncUserTags();
  }

  private async syncUserTags() {
    try {
      await AccountStore.waitForData();

      if (AccountStore.isDemo()) {
        return; // Skip tagging for demo accounts
      }

      const tags = await OneSignal.User.getTags();
      const needsUpdate =
        !tags ||
        tags.mka !== AccountStore.getAccountId() ||
        tags.locationId !== AccountStore.getLocationId();

      if (needsUpdate) {
        OneSignal.User.addTags({
          email: AccountStore.getEmail(),
          mka: AccountStore.getAccountId(),
          locationId: AccountStore.getLocationId(),
          env: Settings.env,
          platform: Platform.OS,
        });
      }
    } catch (err) {
      this.logError('[OneSignal] Failed to sync user tags', err);
    }
  }

  private async requestPermission() {
    try {
      const canRequestPermission =
        await OneSignal.Notifications.canRequestPermission();

      if (canRequestPermission) {
        const permissionResponse =
          await OneSignal.Notifications.requestPermission(true);

        Events.NotificationEvent.trackUserResponseToNotificationPrompt(
          permissionResponse,
        );

        if (!permissionResponse) {
          Events.PermissionDenied.trackEvent(
            'ios.permission.NOTIFICATIONS',
            'Denied',
          );
        }
      }
    } catch (err) {
      this.logError('[OneSignal] requestPermission failed', err);
    }
  }

  private registerListeners() {
    try {
      this.notificationHandlers.foreground = (event) => {
        const notification = event.getNotification();

        if (notification.badge) {
          setTimeout(async () => {
            await notifee.setBadgeCount(0);
          }, 3000);
        }

        if (!notification || (!notification.title && !notification.body)) {
          event.preventDefault();
          return;
        }

        notification.display();
        this.onNotificationReceived(notification);
      };

      OneSignal.Notifications.addEventListener(
        'foregroundWillDisplay',
        this.notificationHandlers.foreground,
      );

      this.notificationHandlers.click = (event) => {
        const notification = event.notification as ExtendedNotification;

        const additionalData = notification?.additionalData || {};
        const rawActionId =
          (event as any)?.result?.actionId ||
          (event as any)?.actionId ||
          (event as any)?.result?.action?.id;
        const actionId =
          typeof rawActionId === 'string'
            ? rawActionId
            : String(rawActionId || '');
        const actionIdLc = actionId.toLowerCase();

        const VIEW_RECEIPT_ACTIONS = [
          'view_receipt',
          'viewreceipt',
          'receipt',
          'open_order',
          'openorder',
        ];
        const isExplicitViewReceipt =
          !!actionId && VIEW_RECEIPT_ACTIONS.includes(actionIdLc);
        const isViewReceiptFlag =
          (additionalData as any).viewReceipt === true ||
          (additionalData as any).view_receipt === true;
        if (isExplicitViewReceipt || isViewReceiptFlag) {
          NavActions.resetSafe(AppRoutes.HomeTab);
          return;
        }

        if ((notification as any).additionalData?.announcementId) {
          const selectedLocationId =
            store.getState().campusLocation?.selectedLocation.locationId;
          const accountHomeLocationId = AccountStore.getLocationId();
          const isSelectedLocationHome =
            selectedLocationId === accountHomeLocationId;

          const isLastRouteInboxMessages =
            NavActions.getLastRoute() === 'Messages';

          const navigateToAnnouncments = () =>
            isLastRouteInboxMessages
              ? NavActions.replace(AppRoutes.Inbox, {
                  selectedTab: 'Announcements',
                })
              : NavActions.nestedPush([AppRoutes.HomeTab], AppRoutes.Inbox, {
                  selectedTab: 'Announcements',
                });

          if (isSelectedLocationHome) {
            navigateToAnnouncments();
          } else {
            store
              .dispatch(
                swicthCurrentCampusesAndLocations({
                  accountId: AccountStore.getAccountId(),
                  locationId: AccountStore.getLocationId(),
                }),
              )
              .then(() => {
                navigateToAnnouncments();
                return;
              });
          }
        }

        if (!notification || (!notification.title && !notification.body)) {

          return;
        }

        this.onNotificationOpened({notification});
      };

      OneSignal.Notifications.addEventListener(
        'click',
        this.notificationHandlers.click,
      );

      try {
        (OneSignal as any).LiveActivities?.addEventListener?.(
          'click',
          (_laEvent: any) => {
            NavActions.resetSafe(AppRoutes.HomeTab);
          },
        );
      } catch (e) {
        this.logError('[OneSignal] failed to set Live Activity listener', e);
      }
    } catch (err) {
      this.logError('[OneSignal] failed to set notification listeners', err);
    }
  }

  onNotificationReceived(notification: OSNotification) {

  }

  onNotificationOpened(payload: {notification: OSNotification}) {

  }
}
